import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useEquipment, useUser } from "@services/servicesGenericsTables";
import { useLaboratory } from "@services/servicesGenericsTables/useLaboratory";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { useTypesUnits } from "@services/servicesGenericsTables/useTypesUnits";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import {
  MAutocomplete,
  MButton,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import ResultsTable from "./resultsTable";
import { KeysTabsAnalysis } from "@app/types/samples.types";
import { TYPE_METHOD_VAPOR } from "@app/pages/samples/utils/constants";
import dayjs from "dayjs";

export interface ResultDataTable {
  label: string;
  value: number;
}

const CalculationComponent = () => {
  const form = useFormContext();
  const { userList } = useUser();
  const { equipmentList } = useEquipment();
  const { laboratoriesList } = useLaboratory();
  const { currentCalculation, saveCalculate, loadingResults } = useAnalysis();
  const { typesUnitsis } = useTypesUnits();
  const filteredCalculation = useFilteredProcedures("CAL");

  const codeAnalysis = form.getValues("typeAnalysis")?.codigo;

  const handleCalculate = async () => {
    await saveCalculate(codeAnalysis as string);
  };

  const typeAnalysis = form.getValues("typeAnalysis")?.codigo;
  const isFe55 = typeAnalysis === KeysTabsAnalysis.Fe55;
  const isVapor = typeAnalysis === KeysTabsAnalysis.H3_V;
  const methodType = form.watch("calculation_method");

  const textButtonCalculation = isVapor
    ? `Calcular ${
        methodType === "ISO" ? TYPE_METHOD_VAPOR.ISO : TYPE_METHOD_VAPOR.CURRIE
      }`
    : "Calcular";

  return (
    <>
      {currentCalculation.index !== undefined && (
        <>
          <ResponsiveFormContainer columns={3}>
            <MAutocomplete
              key="calculation_analyst"
              name="calculation_analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
            />
            <MDatePicker
              key="start_date_time"
              name="start_date_time"
              pickerType="datetime"
              label="Fecha y hora de inicio Toma de muestra"
              disabled
            />
            <MDatePicker
              key="end_date_time"
              name="end_date_time"
              pickerType="datetime"
              label="Fecha y hora fin Toma de muestra"
              disabled
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">
              {"Datos ficha del procesamiento, preparación y contaje:"}
            </Typography>
          </Grid>

          <ResponsiveFormContainer columns={3}>
            {!isVapor ? (
              <MTextField
                key={JSON.stringify(
                  form.getValues(
                    isFe55
                      ? "vial_sample_quantity"
                      : "calculation_amount_sample"
                  )
                )}
                name={
                  isFe55 ? "vial_sample_quantity" : "calculation_amount_sample"
                }
                label={isFe55 ? "Cantidad muestra vial" : "Cantidad de muestra"}
                type="text"
                disabled
                fullWidth
              />
            ) : (
              <MTextField
                key={JSON.stringify(form.getValues("analyzedAirVolume"))}
                label="Volumen de aire (m³)"
                name="analyzedAirVolume"
                type="text"
                disabled
                fullWidth
              />
            )}
            {isFe55 && (
              <MTextField
                key={JSON.stringify(form.getValues("counting_by_one"))}
                name="counting_by_one"
                label="Rendimiento"
                type="text"
                disabled
                fullWidth
              />
            )}
            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_units"))}
              name="calculation_units"
              options={typesUnitsis}
              label="Unidades"
              getOptionLabel={(option) =>
                `${option.codigo} | ${option.descripcion}`
              }
              disabled
            />
            <MTextField
              key={JSON.stringify(form.getValues("calculation_tsie_white"))}
              name="calculation_tsie_white"
              label="TSIE blanco"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              key={JSON.stringify(form.getValues("calculation_tsie_sample"))}
              name="calculation_tsie_sample"
              label="TSIE muestra"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              key={JSON.stringify(form.getValues("calculation_shared"))}
              name="calculation_shared"
              label="Compartida"
              type="text"
              disabled
              fullWidth
            />
            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_equipment"))}
              name="calculation_equipment"
              options={equipmentList}
              label="Equipo de Contaje"
              getOptionLabel={(option) =>
                `${option?.codigo} - ${option?.descripcion}` || ""
              }
              validation={{ required: "El equipo es requerido" }}
              disabled
              multiple={true}
            />
            <MDatePicker
              name="calculation_counting_date"
              pickerType="datetime"
              label="Fecha contaje"
              disabled
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">{"Datos a cumplimentar:"}</Typography>
          </Grid>

          <ResponsiveFormContainer columns={4}>
            <MAutocomplete
              key={JSON.stringify(form.getValues("laboratory"))}
              name="laboratory"
              options={laboratoriesList}
              label="Seleccione un Laboratorio"
              getOptionLabel={(option) =>
                `${option?.codigo} - ${option?.descripcion}` || ""
              }
              validation={{ required: "El laboratorio es requerido" }}
            />

            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_procedure"))}
              name="calculation_procedure"
              options={filteredCalculation}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
            />
            <MDatePicker
              name="calculation_date"
              pickerType="datetime"
              label="Fecha Cálculo"
              // minDate={dayjs(form.watch("calculation_counting_date"))}
            />
          </ResponsiveFormContainer>

          {isVapor && (
            <Controller
              name="calculation_method"
              control={form.control}
              key={JSON.stringify(form.getValues("calculation_method"))}
              render={({ field }) => (
                <RadioGroup {...field} row sx={{ mt: 2 }}>
                  <FormControlLabel
                    value="ISO"
                    control={<Radio />}
                    label={TYPE_METHOD_VAPOR.ISO}
                    disabled={form.getValues("idResultPreload")}
                  />
                  <FormControlLabel
                    value="CURRIE"
                    control={<Radio />}
                    label={TYPE_METHOD_VAPOR.CURRIE}
                    disabled={form.getValues("idResultPreload")}
                  />
                </RadioGroup>
              )}
            />
          )}

          <Box
            sx={{
              padding: 3,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              border: "1px solid #ddd",
              mt: 2,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={false} sm={2} />
              <Grid item xs={4}>
                <Typography variant="body1">
                  {isFe55 ? "cpm muestra final" : "cpm brutas de la muestra"}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <MTextField
                  key={JSON.stringify(form.getValues("sample_gross_cpm"))}
                  name="sample_gross_cpm"
                  type="text"
                  disabled
                  fullWidth
                  stylesInput={{ "& .MuiInputBase-input": { padding: 1 } }}
                />
              </Grid>
              <Grid item xs={false} sm={4} />

              <Grid item xs={false} sm={2} />
              <Grid item xs={4}>
                <Typography variant="body1">
                  {isFe55 ? "cpm blanco final" : "cpm brutas del blanco"}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <MTextField
                  key={JSON.stringify(form.getValues("gross_target_cpm"))}
                  name="gross_target_cpm"
                  type="text"
                  disabled
                  fullWidth
                  stylesInput={{ "& .MuiInputBase-input": { padding: 1 } }}
                />
              </Grid>
              <Grid item xs={false} sm={4} />

              <Grid item xs={false} sm={2} />
              <Grid item xs={4}>
                <Typography variant="body1">
                  Tiempo total de contaje de la muestra (min)
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <MTextField
                  key={JSON.stringify(form.getValues("total_time_counting"))}
                  name="total_time_counting"
                  type="text"
                  disabled
                  fullWidth
                  stylesInput={{ "& .MuiInputBase-input": { padding: 1 } }}
                />
              </Grid>
              <Grid item xs={false} sm={4} />

              <Grid item xs={false} sm={2} />
              <Grid item xs={4}>
                <Typography variant="body1">
                  Tiempo total de contaje del blanco (min)
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <MTextField
                  key={JSON.stringify(
                    form.getValues("total_white_counting_time")
                  )}
                  name="total_white_counting_time"
                  type="text"
                  disabled
                  fullWidth
                  stylesInput={{ "& .MuiInputBase-input": { padding: 1 } }}
                />
              </Grid>
              <Grid item xs={false} sm={4} />
            </Grid>
          </Box>

          {currentCalculation.id !== undefined && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
              }}
            >
              <MButton
                onClick={handleCalculate}
                text={textButtonCalculation}
                color="secondary"
                loading={loadingResults}
                disabled={!!form.watch("idResultPreload")}
              />
            </Box>
          )}

          <ResultsTable
            key={currentCalculation.index}
            isCurrier={
              isVapor
                ? methodType?.toUpperCase() ===
                  TYPE_METHOD_VAPOR.CURRIE?.toUpperCase()
                : false
            }
          />
        </>
      )}
    </>
  );
};

export default CalculationComponent;
